import React, { useEffect, useState, useRef } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

function BranchSelector({ API_URL }) {
    const { selectedBranch, setSelectedBranch, branches, loadBranches, isLoading } = useBranch();
    const { token, currentUser } = useAuth();
    const { t } = useLanguage();
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (token && branches.length === 0) {
            loadBranches(token, API_URL);
        }
    }, [token, API_URL, branches.length, loadBranches]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedBranchData = branches.find(b => b.id === selectedBranch);

    // Hide if user is restricted to a specific branch (e.g. staff/manager)
    if (currentUser?.branch_id) {
        return null;
    }

    return (
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 p-2.5 sm:p-4 mb-3 transition-all hover:shadow-lg">
            <div className="flex items-center gap-3">
                {/* Icon Section */}
                <div className="hidden sm:flex items-center justify-center w-10 h-10 bg-emerald-50 rounded-xl border border-emerald-100 flex-shrink-0">
                    <i className="fas fa-university text-emerald-600"></i>
                </div>
 
                <div className="flex-1 min-w-0 relative" ref={dropdownRef}>
                    <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">
                        {t('selectBranch') || 'Branch Location'}
                    </label>
 
                    <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        disabled={isLoading}
                        className="w-full bg-gray-50 border-2 border-gray-100 text-gray-800 font-bold text-xs md:text-sm rounded-xl px-3.5 py-2.5 flex items-center justify-between group hover:border-emerald-500 hover:bg-white transition-all shadow-sm"
                    >
                        <div className="flex items-center gap-2 truncate">
                            <i className={`fas ${selectedBranch ? 'fa-store' : 'fa-globe'} text-emerald-500 opacity-60 text-[10px]`}></i>
                            <span className="truncate font-bold">
                                {isLoading ? `${t('loading')}...` : (selectedBranch ? (selectedBranchData?.name || t('branch')) : t('allBranchesCombined') || 'All Branches')}
                            </span>
                        </div>
                        <i className={`fas fa-chevron-down text-[10px] text-gray-400 transition-transform duration-300 ${showDropdown ? 'rotate-180 text-emerald-600' : ''}`}></i>
                    </button>
 
                    {showDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-100 py-1.5 z-[100] animate-fade-in max-h-64 overflow-y-auto overflow-x-hidden scrollbar-hide" style={{ backgroundColor: '#ffffff' }}>
                            <div className="px-3 py-1.5 border-b border-gray-50 mb-1">
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('chooseLocation') || 'Choose Location'}</p>
                            </div>
 
                            {branches.length > 1 && (
                                <button
                                    onClick={() => { setSelectedBranch(null); setShowDropdown(false); }}
                                    className={`w-full text-left px-3 py-2.5 text-xs hover:bg-emerald-50 transition-all flex items-center gap-3 transform hover:translate-x-1 duration-200 ${!selectedBranch ? 'text-emerald-600 font-black bg-emerald-50' : 'text-gray-700 font-bold'}`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${!selectedBranch ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-gray-100 text-gray-400'}`}>
                                        <i className="fas fa-globe text-[10px]"></i>
                                    </div>
                                    <span className="truncate">{t('allBranchesCombined') || 'All Branches'}</span>
                                    {!selectedBranch && <i className="fas fa-check-circle ml-auto text-emerald-600 text-[10px]"></i>}
                                </button>
                            )}
 
                            {branches.map(branch => (
                                <button
                                    key={branch.id}
                                    onClick={() => { setSelectedBranch(branch.id); setShowDropdown(false); }}
                                    className={`w-full text-left px-3 py-2.5 text-xs hover:bg-emerald-50 transition-all flex items-center gap-3 transform hover:translate-x-1 duration-200 ${selectedBranch === branch.id ? 'text-emerald-600 font-black bg-emerald-50' : 'text-gray-700 font-bold'}`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${selectedBranch === branch.id ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-gray-100 text-gray-400'}`}>
                                        <i className="fas fa-store text-[10px]"></i>
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="truncate">{branch.name}</span>
                                        <span className={`text-[8px] font-bold uppercase tracking-tighter ${branch.is_active ? 'text-emerald-500' : 'text-gray-400'}`}>
                                            {branch.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    {selectedBranch === branch.id && <i className="fas fa-check-circle ml-auto text-emerald-600 text-[10px]"></i>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
 
                {/* Counter Badge */}
                <div className="hidden md:flex flex-col items-end gap-1 px-4 border-l border-gray-100 flex-shrink-0">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Live Branches</span>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                        <span className="text-xs font-black text-gray-700">{branches.length} Registered</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BranchSelector;
